import { Component, input, OnInit, viewChildren } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TNil } from '@app/app.interface';
import { ICustomCtaValue, IGlobalCtaValue } from '@campaign-form/campaign-form.interface';
import { CtaDetailsService } from '@campaign-form/form-fields/creators-and-ctas/cta-details.service';
import {
  CustomCtaBaseDirective,
  ECustomFieldType,
  ICustomValueConfig,
} from '@core/directives/custom-cta-base.directive';
import { IFindingCardsObjective } from '@cta-analytics-dialog/deliverable-analytics-content/finding-cards/finding-card.interface';
import { EDeliverableType, EPlatformFeature } from '@enums/campaign.enum';
import { TranslateModule } from '@ngx-translate/core';
import { AnalyticsStatusBadgeComponent } from '@shared/components/analytics-status-badge/analytics-status-badge.component';
import { CustomRemoveButtonComponent } from '@shared/components/custom-remove-button/custom-remove-button.component';
import { EditableTitleComponent } from '@shared/components/editable-title/editable-title.component';
import { FileUploaderComponent } from '@shared/components/file-uploader/file-uploader.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { imageExtensionValidator } from '@shared/validators/image-extension.validator';
import { FormUtilitiesService } from '@utilities/form-utilities.service';
import { v4 as uuidv4 } from 'uuid';
import { DeliverableAdvancedButtonComponent } from '../advanced-button/deliverable-advanced-button.component';
import { DeliverableAdvancedSettingsSectionComponent } from '../advanced-settings-section/deliverable-advanced-settings-section.component';
import { IDeliverableImageForm } from '../deliverable-list.interface';

@Component({
  imports: [
    DeliverableAdvancedButtonComponent,
    DeliverableAdvancedSettingsSectionComponent,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatErrorComponent,
    FileUploaderComponent,
    MatCheckboxModule,
    MatTooltipModule,
    EditableTitleComponent,
    MatExpansionModule,
    IconComponent,
    CustomRemoveButtonComponent,
    AnalyticsStatusBadgeComponent,
  ],
  selector: 'app-product-placement',
  templateUrl: './product-placement.component.html',
})
export class ProductPlacementComponent extends CustomCtaBaseDirective implements OnInit {
  public readonly platformFeature = input<EPlatformFeature | null | undefined>(null);
  public readonly index = input<number | undefined>();
  public readonly analyticsObjective = input<IFindingCardsObjective | null>(null);

  protected override readonly customValueConfig: ICustomValueConfig = {
    componentFields: {
      getForm: () => this.form(),
      managedFields: [
        { name: 'productName', type: ECustomFieldType.string },
        { name: 'id', type: ECustomFieldType.string },
        { name: 'sponsoredContentRelevant', type: ECustomFieldType.boolean },
      ],
    },
    nestedArray: {
      getFormArray: () => this.images,
      managedFields: [
        { name: 'imageId', type: ECustomFieldType.string },
        { name: 'id', type: ECustomFieldType.string },
      ],
    },
  };

  private static readonly HIGHLIGHT_NAMESPACE = 'imageUploader';

  private readonly fileUploaders = viewChildren<FileUploaderComponent>('fileUploaderComponent');

  private fileUploaderDeleted = false;

  public get images(): FormArray<FormGroup<IDeliverableImageForm>> {
    return this.form().get('images') as FormArray<FormGroup<IDeliverableImageForm>>;
  }

  public isNewlyAddedImage(id: string): boolean {
    return FormUtilitiesService.isNewlyHighlightedElement(id, ProductPlacementComponent.HIGHLIGHT_NAMESPACE);
  }

  public addProductPlacementImage(): void {
    const newImageForm = new FormGroup<IDeliverableImageForm>({
      customCtaValues: new FormControl<ICustomCtaValue[]>([], { nonNullable: true }),
      exclusiveTo: new FormControl<string | null>(this.isCustomMode() ? this.creatorId()! : null),
      globalCtaValues: new FormControl<IGlobalCtaValue[] | null>(null),
      id: new FormControl<string | null>(uuidv4()),
      imageFile: new FormControl<File | null>(null, [Validators.required, imageExtensionValidator()]),
      imageId: new FormControl<string | null>(null),
    });

    this.images.push(newImageForm);
    this.initializeNestedForm(newImageForm, [{ name: 'imageId', type: ECustomFieldType.string }]);
    this.trackNestedArrayItemAdded('imageId', this.images.length - 1);
    this.isChildDeleteDisabled.set(false);
    this.lastActiveChildElementDetails.set(
      CtaDetailsService.getLastActiveElementCreatorNames(this.images, this.isCustomMode(), this.creators()),
    );

    FormUtilitiesService.highlightNewElement(newImageForm, ProductPlacementComponent.HIGHLIGHT_NAMESPACE);
  }

  public removeFileUploader(id: string): void {
    const imageFormIndex = this.images.controls.findIndex((control) => control.get('id')?.value === id);
    const form = this.images.at(imageFormIndex) as FormGroup;

    const dialogRef = CtaDetailsService.openRemoveConfirmationDialog(
      this.dialog,
      this.translate,
      form,
      this.creators(),
      'productPlacementImage',
    );

    if (dialogRef) {
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.removeFileUploaderOperations(id);
        }
      });

      return;
    }

    this.removeFileUploaderOperations(id);
  }

  public onImageSelected(event: { file: File; id: string }, id: string): void {
    this.setImage(event.file, event.id, id);
  }

  public onImageRemoved(id: string): void {
    if (this.fileUploaderDeleted) {
      this.fileUploaderDeleted = false;

      const imageFormIndex = this.images.controls.findIndex((control) => control.get('id')?.value === id);
      const form = this.images.at(imageFormIndex) as FormGroup;
      const removedValue = form.get('imageId')?.value as string | null;

      this.trackNestedArrayItemRemoved('imageId', imageFormIndex, removedValue);
      this.cleanupNestedForm(form);
      this.images.removeAt(imageFormIndex);
      this.checkNestedFormDeleteDisableConditions(this.images);

      return;
    }

    this.setImage(null, null, id);
    this.checkNestedFormDeleteDisableConditions(this.images);
  }

  public override onDeleteClick(exclusiveTo: string | TNil): void {
    const dialogRef = super.onDeleteClick(exclusiveTo);

    if (dialogRef) {
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.deleteOperations();
        }
      });

      return;
    }

    this.deleteOperations();
  }

  private setImage(file: File | null, image: string | null, id: string): void {
    const imageGroup = this.images.controls.find((control) => control.get('id')?.value === id) as FormGroup;

    imageGroup.patchValue({ imageFile: file, imageId: image });
    imageGroup.get('imageFile')?.markAsDirty();
    imageGroup.get('imageId')?.markAsDirty();
    imageGroup.updateValueAndValidity();
  }

  private removeFileUploaderOperations(id: string): void {
    this.fileUploaderDeleted = true;
    this.isChildDeleteDisabled.set(true);

    this.fileUploaders()
      .find((fileUploader) => fileUploader.id() === id)
      ?.onRemoveImage();
  }

  public override ngOnInit(): void {
    super.ngOnInit();
    this.autoOpenAdvancedSettingsIfNeeded(this.platformFeature(), this.form().get('type')?.value as EDeliverableType);
  }

  private deleteOperations(): void {
    this.fileUploaders().forEach((uploader) => uploader.onRemoveImage());
    this.delete.emit();
  }
}
